import { z } from 'zod';
import { isNegative, isZero, money, MONEY_PATTERN, type Money } from '@miraki/money';

/**
 * Money on the wire is ALWAYS a string — rule 4. This schema is the only
 * sanctioned way money enters the system from JSON, and it never accepts a number.
 */
export const zMoney = z
  .string()
  .regex(MONEY_PATTERN, 'must be a decimal amount with at most 4 fractional digits')
  .transform((s): Money => money(s));

/**
 * The rule "this amount must be greater than zero", in ONE place.
 *
 * It existed twice, with different answers: A-41's own record-a-term route refused a zero or
 * negative fee, and the approval path used `zMoney` — which checks the decimal FORMAT only —
 * and so accepted both for the same field. A fee is not the kind of thing to have two
 * definitions of, and this project has now had four figures that did.
 *
 * A predicate as well as a schema, because the two callers need different output types: one
 * wants a parsed `Money`, the other keeps the string. Same rule either way.
 */
export const isPositiveAmount = (s: string): boolean =>
  // Zod runs a refine even when the regex has already failed, so only parse a well-formed one.
  !MONEY_PATTERN.test(s) || (!isZero(money(s)) && !isNegative(money(s)));

/** Money that must be greater than zero. A fee, a price, a budget — never a balance. */
export const zPositiveMoney = z
  .string()
  .regex(MONEY_PATTERN, 'must be a decimal amount with at most 4 fractional digits')
  .refine(isPositiveAmount, 'must be greater than zero')
  .transform((s): Money => money(s));

/** UTC instant. Stored timestamptz, rendered in the viewer's zone — D49. */
export const zInstant = z.string().datetime({ offset: true });

export const zPortalCode = z.string().min(2).max(32).regex(/^[a-z0-9_-]+$/);
export const zCurrency = z.string().length(3).regex(/^[A-Z]{3}$/);
export const zLocale = z.string().min(2).max(10);

/** module.action — RL-01. Field-level permissions are out of scope by decision. */
export const zPermission = z.string().regex(/^[a-z_]+\.[a-z_]+$/);

export const zId = z.string().uuid();
export const zPagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(25),
});
